import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useColorMode } from '../context/ColorModeContext';
import { useTenant } from '../context/TenantContext';
import { useCompactLayout } from '../hooks/useCompactLayout';
import { downComponents } from '../services/routing/api';
import {
  useEngineStatusQuery,
  useHealthQuery,
  useTenantsQuery
} from '../services/routing/queries';

const NAV = [
  { label: 'Runs', path: '/runs' },
  { label: 'New run', path: '/runs/new' },
  { label: 'Metrics', path: '/metrics' },
  { label: 'World', path: '/world' },
  { label: 'Config', path: '/config' },
  { label: 'Admin', path: '/admin' },
  { label: 'API guide', path: '/docs' }
];

const TenantPicker = ({ grow = false }: { grow?: boolean }) => {
  const { tenant, setTenant } = useTenant();
  const { data } = useTenantsQuery();
  const options = [...new Set([...(data?.tenants ?? []), tenant])];
  return (
    <TextField
      select
      size="small"
      label="Tenant"
      value={tenant}
      onChange={event => setTenant(event.target.value)}
      SelectProps={{ SelectDisplayProps: { 'aria-label': 'active tenant' } }}
      sx={{ minWidth: 160, flexGrow: grow ? 1 : 0 }}
    >
      {options.map(name => (
        <MenuItem key={name} value={name}>
          {name}
        </MenuItem>
      ))}
    </TextField>
  );
};

const ColorModeToggle = () => {
  const { mode, toggle } = useColorMode();
  const next = mode === 'dark' ? 'light' : 'dark';
  return (
    <Tooltip title={`Switch to ${next} theme`}>
      <IconButton aria-label={`switch to ${next} theme`} onClick={toggle} size="small">
        {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

const ALERT_CHIP = {
  border: 2,
  borderColor: 'text.primary',
  fontWeight: 800,
  bgcolor: 'transparent'
};
const OK_CHIP = { bgcolor: 'text.primary', color: 'background.default' };
const WARN_CHIP = { border: 1, borderColor: 'text.secondary', bgcolor: 'transparent' };

const HealthChip = () => {
  const { data, isError } = useHealthQuery();
  // Only a request that never got a response is "unreachable" — a 503 still answers with DOWN.
  if (isError) return <Chip label="server unreachable" size="small" sx={ALERT_CHIP} />;
  if (!data) return <Chip label="health: …" size="small" variant="outlined" />;

  const down = downComponents(data);
  const label = down.length ? `health: ${data.status} (${down.join(', ')})` : `health: ${data.status}`;
  return (
    <Tooltip title={down.length ? `${down.join(', ')} — see Admin › service health` : ''}>
      <Chip label={label} size="small" sx={data.status === 'UP' ? OK_CHIP : WARN_CHIP} />
    </Tooltip>
  );
};

/** Whether the engine this tenant is configured to call — at its configured URL — answers. */
const EngineChip = () => {
  const { tenant } = useTenant();
  const { data, isError, isLoading } = useEngineStatusQuery(tenant);
  if (isLoading) return <Chip label="engine: …" size="small" variant="outlined" />;
  if (isError || !data) return null;

  const label = data.reachable
    ? `engine: ${data.engine}${data.routed ? '' : ' — no path'}`
    : `${data.engine} unreachable`;
  const style = data.reachable ? (data.routed ? OK_CHIP : WARN_CHIP) : ALERT_CHIP;
  const detail = data.reachable
    ? `${data.url} · ${data.latency_ms} ms · ${data.engine_source}`
    : `${data.url} did not answer — planning would fall back to straight-line estimates`;

  return (
    <Tooltip title={detail}>
      <Chip label={label} size="small" sx={style} />
    </Tooltip>
  );
};

const Brand = () => (
  <Typography
    variant="h6"
    sx={{ whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.14em' }}
  >
    Myle Routing
  </Typography>
);

/** The tab bar as a drawer: a phone has no room for seven tabs beside the brand. */
const NavDrawer = ({
  open,
  active,
  onClose
}: {
  open: boolean;
  active: string;
  onClose: () => void;
}) => (
  <Drawer open={open} onClose={onClose} PaperProps={{ sx: { width: 240, border: 0 } }}>
    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Brand />
    </Box>
    <List component="nav" aria-label="pages">
      {NAV.map(item => (
        <ListItemButton
          key={item.path}
          component={Link}
          to={item.path}
          selected={item.path === active}
          onClick={onClose}
        >
          <ListItemText primary={item.label} />
        </ListItemButton>
      ))}
    </List>
  </Drawer>
);

export const Layout = () => {
  const location = useLocation();
  const compact = useCompactLayout();
  const [navOpen, setNavOpen] = useState(false);
  const active =
    NAV.filter(item => location.pathname.startsWith(item.path))
      .sort((a, b) => b.path.length - a.path.length)[0]?.path ?? '/runs';
  const activeLabel = NAV.find(item => item.path === active)?.label ?? '';

  return (
    <Box>
      <AppBar position="static" color="default" elevation={1}>
        {compact ? (
          <Toolbar sx={{ gap: 1, flexWrap: 'wrap', px: 1.5, py: 1 }}>
            <IconButton aria-label="open navigation" onClick={() => setNavOpen(true)} edge="start">
              <MenuIcon />
            </IconButton>
            <Brand />
            {/* The tabs are in the drawer, so the page has to name itself somewhere. */}
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }} noWrap>
              · {activeLabel}
            </Typography>
            <ColorModeToggle />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ flexBasis: '100%', pb: 0.5 }}
            >
              <TenantPicker grow />
              <EngineChip />
              <HealthChip />
            </Stack>
            <NavDrawer open={navOpen} active={active} onClose={() => setNavOpen(false)} />
          </Toolbar>
        ) : (
          <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
            <Brand />
            <Tabs value={active} sx={{ flexGrow: 1 }}>
              {NAV.map(item => (
                <Tab
                  key={item.path}
                  label={item.label}
                  value={item.path}
                  component={Link}
                  to={item.path}
                />
              ))}
            </Tabs>
            <TenantPicker />
            <EngineChip />
            <HealthChip />
            <ColorModeToggle />
          </Toolbar>
        )}
      </AppBar>
      <Container
        maxWidth={false}
        sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 }, maxWidth: 1500 }}
      >
        <Outlet />
      </Container>
    </Box>
  );
};

export default Layout;
